import React, { useState } from 'react';
import CrudPage from '../../../components/CrudPage';
import useLookups from '../../../hooks/useLookups';
import { Modal, Button, Badge, PhotoThumb } from '../../../components/common';
import PhotoUpload from './PhotoUpload';
import { AUDIT_COLUMNS } from '../../../config/columns';
import { apiError } from '../../../services/api';
import { customerService, nationalityService, getPhotoUrl } from '../../../services/resources';

export default function CustomerPage({ onToast }) {
  const [preview, setPreview] = useState(null); // full-size photo modal
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      await customerService.export();
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setExporting(false);
    }
  };

  const lookups = useLookups({
    nationalities: { service: nationalityService, labelKey: (r) => r.nationality_name },
  });
  const labelFor = (name, id) => lookups[name]?.find((o) => o.value === id)?.label ?? id;

  const toggleBlock = async (row, reload) => {
    try {
      if (row.is_blocked) {
        await customerService.unblock(row.id);
        onToast?.('Customer unblocked');
      } else {
        await customerService.block(row.id);
        onToast?.('Customer blocked');
      }
      reload();
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    }
  };

  // Fetch as a blob so the download works even though the image is served
  // cross-origin (localhost:5000 vs 3000), where the anchor download hint alone
  // would be ignored.
  const downloadPhoto = async () => {
    try {
      const res = await fetch(preview);
      const blob = await res.blob();
      const ext = blob.type === 'image/png' ? 'png' : 'jpg';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customer-photo.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      onToast?.('Could not download photo', 'error');
    }
  };

  return (
    <>
      <CrudPage
        title="Customer"
        formCode="CUSTOMER"
        service={customerService}
        onToast={onToast}
        lookups={lookups}
        searchKeys={['customer_name', 'ktp']}
        headerActions={
          <Button variant="secondary" onClick={doExport} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        }
        columns={[
          { key: 'customer_name', label: 'Name' },
          { key: 'ktp', label: 'KTP / Passport' },
          { key: 'nationality_id', label: 'Nationality', render: (r) => labelFor('nationalities', r.nationality_id) },
          {
            key: 'photo',
            label: 'Photo',
            render: (r) => {
              const thumbUrl = `${getPhotoUrl('customer', r.id, 'thumb')}?t=${r.updated_on}`;
              return (
                <PhotoThumb
                  key={thumbUrl}
                  src={thumbUrl}
                  onClick={() => setPreview(`${getPhotoUrl('customer', r.id)}?t=${r.updated_on}`)}
                />
              );
            },
          },
          {
            key: 'is_blocked',
            label: 'Status',
            render: (r) =>
              r.is_blocked ? <Badge tone="red">Blocked</Badge> : <Badge tone="green">Active</Badge>,
          },
          ...AUDIT_COLUMNS,
        ]}
        fields={[
          // Name is locked after creation and stored uppercase.
          { key: 'customer_name', label: 'Customer Name', type: 'text', required: true, immutableOnEdit: true, uppercase: true },
          { key: 'ktp', label: 'KTP / Passport', type: 'text' },
          { key: 'nationality_id', label: 'Nationality', type: 'select', required: true, lookup: 'nationalities' },
          { key: 'address', label: 'Address', type: 'textarea' },
          { key: 'npwp', label: 'NPWP', type: 'text' },
          { key: 'job', label: 'Job', type: 'text' },
        ]}
        rowActions={(row, { reload }) => (
          <Button
            size="sm"
            variant={row.is_blocked ? 'secondary' : 'ghost'}
            onClick={() => toggleBlock(row, reload)}
          >
            {row.is_blocked ? 'Unblock' : 'Block'}
          </Button>
        )}
        renderFormExtra={({ editing, reload }) =>
          editing ? (
            <PhotoUpload customer={editing} onUploaded={reload} onToast={onToast} />
          ) : (
            <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-500">
              Save the customer first, then re-open to upload a photo.
            </p>
          )
        }
      />

      <Modal
        open={!!preview}
        onClose={() => setPreview(null)}
        title="Customer Photo"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPreview(null)}>
              Close
            </Button>
            <Button onClick={downloadPhoto}>Download</Button>
          </>
        }
      >
        {preview && <img src={preview} alt="customer" className="mx-auto max-h-[70vh] rounded" />}
      </Modal>
    </>
  );
}
